import type { PublicSitePlanBlock, PublicSitePlanPayload } from '../../../services/superPickerPublicApi';
import { parseUnitNumber } from '../../../utils/parseUnitNumber';
import type { PublicUnitForMenu } from '../unit/types';

const normalizeStackKey = (stack: string | number | null | undefined): string => {
  if (stack == null) return '';
  return String(stack).trim();
};

const buildCompareStackKeys = (
  stack: string | number | null | undefined,
  blockNumber: string | null | undefined,
): Set<string> => {
  const keys = new Set<string>();
  const stackKey = normalizeStackKey(stack);
  if (stackKey) keys.add(stackKey);
  const blockDigits = (blockNumber ?? '').replace(/\D/g, '');
  if (blockDigits && stackKey) {
    keys.add(`${blockDigits}-${stackKey}`);
  }
  return keys;
};

export interface CompareSitePlanMarker {
  left: number;
  top: number;
  width: number;
  height: number;
  label: string;
}

/**
 * Resolve the highlighted stack marker for a unit on a project site plan.
 * Mirrors `getCompareSitePlanMarker` in `SuperPickerApp.tsx`.
 */
export const getCompareSitePlanMarker = (
  sitePlan: PublicSitePlanPayload | null | undefined,
  unit: PublicUnitForMenu,
): CompareSitePlanMarker | null => {
  if (!sitePlan) return null;

  const { stack } = parseUnitNumber(unit.unitNumber, unit.floorNumber);
  if (stack == null) return null;

  const unitBlockDigits = (unit.blockNumber || '').replace(/\D/g, '');
  const unitKeys = buildCompareStackKeys(stack, unit.blockNumber);
  if (unitKeys.size === 0) return null;

  const stacks = sitePlan.blocks.filter((block) => block.stack != null && String(block.stack).trim() !== '');
  const doesMatch = (block: PublicSitePlanBlock) => {
    const blockKeys = buildCompareStackKeys(block.stack, block.blockNumber);
    return Array.from(blockKeys).some((key) => unitKeys.has(key));
  };

  const preferredMatch = stacks.find((block) => {
    if (!doesMatch(block)) return false;
    if (!unitBlockDigits) return false;
    const blockDigits = (block.blockNumber || '').replace(/\D/g, '');
    return blockDigits === unitBlockDigits;
  });
  const match = preferredMatch ?? stacks.find(doesMatch);
  if (!match) return null;

  const bounds = sitePlan.bounds;
  const left = bounds ? bounds.x + (match.x / 100) * bounds.width : match.x;
  const top = bounds ? bounds.y + (match.y / 100) * bounds.height : match.y;
  const width = bounds ? (match.width / 100) * bounds.width : match.width;
  const height = bounds ? (match.height / 100) * bounds.height : match.height;

  return {
    left,
    top,
    width,
    height,
    label: normalizeStackKey(stack),
  };
};
